import React, { useState } from "react";
import styles from "./DonationForm.module.css";
import {
  createDonationPaymentData,
  submitDonationPayment,
} from "@/services/DonationPaymentService";
import { toast } from "react-hot-toast";

const DONATION_AMOUNTS = [2500, 5000, 7500];
const MINIMUM_AMOUNT = 50;
const MAXIMUM_DIGITS = 7;

interface DonationFormProps {
  onSubmit?: (amount: number) => void;
  userEmail?: string;
  userName?: string;
}

type PaymentStep = "initial" | "processing" | "redirecting" | "error";

const DonationForm: React.FC<DonationFormProps> = ({
  onSubmit,
  userEmail,
  userName,
}) => {
  const [selected, setSelected] = useState<number | null>(5000);
  const [other, setOther] = useState("");
  const [error, setError] = useState("");
  const [paymentStep, setPaymentStep] = useState<PaymentStep>("initial");

  const handleRadio = (amount: number) => {
    setSelected(amount);
    setOther("");
    setError("");
  };

  const handleOtherChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    // Check if the input exceeds 7 digits
    if (value.length > MAXIMUM_DIGITS) {
      return; // Don't update if it exceeds the limit
    }

    setOther(value);
    setSelected(null);
    setError("");

    // Validate minimum amount as user types
    if (value) {
      const amount = parseInt(value, 10);
      if (!isNaN(amount) && amount < MINIMUM_AMOUNT) {
        setError(`Minimum donation amount is R${MINIMUM_AMOUNT}`);
      } else {
        setError("");
      }
    }
  };

  const formatFee = (amount: number): string => {
    return amount.toFixed(2).replace(/\.00$/, "");
  };

  const validateAmount = (amount: number | null): string | null => {
    if (!amount) {
      return "Please select or enter an amount";
    }
    if (amount < MINIMUM_AMOUNT) {
      return `Minimum donation amount is R${MINIMUM_AMOUNT}`;
    }
    if (amount.toString().length > MAXIMUM_DIGITS) {
      return `Maximum donation amount cannot exceed ${MAXIMUM_DIGITS} digits`;
    }
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    let amount = selected;

    // Validate amount
    if (other) {
      amount = parseInt(other, 10);
      if (isNaN(amount)) {
        setError("Please enter a valid amount");
        return;
      }
    }

    const validationError = validateAmount(amount);
    if (validationError) {
      setError(validationError);
      return;
    }

    if (!userEmail || !userName) {
      setError("User information is missing");
      return;
    }

    // At this point, amount is guaranteed to be a number due to validation
    const finalAmount = amount as number;

    setError("");
    setPaymentStep("processing");

    try {
      const formattedAmount = Number(formatFee(finalAmount));
      const paymentData = await createDonationPaymentData(
        formattedAmount,
        userEmail,
        userName
      );

      localStorage.setItem(
        "donationDetails",
        JSON.stringify({
          amount: formattedAmount,
          email: userEmail,
          name: userName,
          reference: paymentData.reference,
        })
      );

      setPaymentStep("redirecting");
      await submitDonationPayment(paymentData);

      if (onSubmit) {
        onSubmit(finalAmount);
      }
    } catch (error) {
      console.error("Payment processing error:", error);
      setPaymentStep("error");
      setError("Failed to process payment. Please try again.");
      toast.error("Payment processing failed. Please try again.");
    }
  };

  const getButtonText = () => {
    switch (paymentStep) {
      case "processing":
        return "Processing Payment...";
      case "redirecting":
        return "Redirecting to Payment Gateway...";
      case "error":
        return "Try Again";
      default:
        return "Change a Life Today";
    }
  };

  const isButtonDisabled =
    paymentStep === "processing" || paymentStep === "redirecting";

  return (
    <form className={styles.donationForm} onSubmit={handleSubmit}>
      <h2 className={styles.heading}>How much do you want to donate?</h2>
      <p className={styles.subheading}>
        Fund a student&apos;s future &mdash; 100% goes to them, and it&apos;s
        tax-free
      </p>

      <div className={styles.optionsRow}>
        {DONATION_AMOUNTS.map((amt) => (
          <label
            key={amt}
            className={`${styles.radioBox} ${
              selected === amt ? styles.selected : ""
            }`}
          >
            <input
              type="radio"
              name="donation"
              value={amt}
              checked={selected === amt}
              onChange={() => handleRadio(amt)}
              disabled={isButtonDisabled}
            />
            <strong>R {amt.toLocaleString()}</strong>
            {amt === 5000 && (
              <span className={styles.badge}>ALUMNI&apos;S FAVORITE</span>
            )}
          </label>
        ))}
      </div>
      <input
        className={styles.otherInput}
        type="number"
        min={MINIMUM_AMOUNT}
        max={10 ** MAXIMUM_DIGITS - 1}
        placeholder={`Other amount`}
        value={other}
        onChange={handleOtherChange}
        disabled={isButtonDisabled}
      />
      {error && <div className={styles.error}>{error}</div>}
      <button
        type="submit"
        className={`${styles.submitBtn} ${
          paymentStep === "error" ? styles.error : ""
        }`}
        disabled={isButtonDisabled}
      >
        {getButtonText()}
      </button>
      <div className={styles.impactText}>
        Thanks to donors like you, <b>over 234,566 students</b> have pursued
        their dreams
      </div>
    </form>
  );
};

export default DonationForm;
